import { AddressType } from '@ukpostbox/client-data/lib/Types/AddressTypes';
import { SendsAddressDetailType } from '@ukpostbox/client-data/lib/Types/StandaloneSendsTypes';
import {
	AlertBanner,
	ButtonSubmit,
	Colour,
	ColourStateSolid,
	FlexAlign,
	FlexGap,
	FlexJustify,
	StackVertical,
} from '@ukpostbox/frontend-framework';
import React, { useCallback, useEffect, useState } from 'react';
import { FieldValues, FormProvider, useForm } from '@ukpostbox/frontend-framework/lib/ReactHookForm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/pro-solid-svg-icons/faPaperPlane';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation';
import { useParams } from 'react-router';
import {
	ipmQueryClient,
	sendMail,
	updateSendsPageAddress,
	useUpdateSendsDetail,
	UserFileCategory,
	uploadFile,
} from '@ukpostbox/client-data';
import { useSendMail } from '../../Providers/SendMailProvider';
import { SendsFileControl } from '../../../SendMail/Components/Controls/SendsFileControl';
import { RestrictedAddressControl } from '../../ProfilesUser/Components/Controls/RestrictedAddressControl';
import { RestrictedPostageControl } from '../../ProfilesUser/Components/Controls/RestrictedPostageControl';
import SendProcessingWizardModal from './Modals/SendProcessingWizardModal';
import SendMultipleProcessingWizardModal from './Modals/SendMultipleProcessingWizardModal';
import { SendDataProcessType } from '../Types/SendsFormTypes';

function StandaloneSendForm() {
	const { pageId } = useParams();

	const {
		address,
		postage,
		sendPageData,
		sendPageLoading,
		sendPageError,
		sendPageID,
		UpdatePageId,
		UpdateAddress,
		ResetSendMail,
	} = useSendMail();

	const formMethods = useForm({
		defaultValues: {
			address: -1,
			postage: -1,
			file: null,
		},
	});

	const [sendError, setSendError] = useState<string | null>(null);

	const [sendProcessData, setSendProcessData] = useState<SendDataProcessType>({
		form: null,
		address: address?.value,
		postage: postage?.value,

		sendProcessOpen: false,

		uploadFileActive: false,
		sendDataActive: false,

		postingData: false,

		sendComplete: false,
	});

	const {
		handleSubmit,
		reset: formReset,
		formState: { isValid },
	} = formMethods;

	const sendsTemplates = useUpdateSendsDetail();

	const HandleProcessStart = (data: FieldValues) => {
		setSendError(null);
		setSendProcessData({
			...sendProcessData,
			form: { ...data, sendsPageId: sendPageID },
			sendProcessOpen: true,
			address: address?.value,
			postage: postage?.value,
		});
	};

	const ForwardAddressChangeData = async (patchData: any, uiData: any, nextValue: number, addressCount: number) => {
		await updateSendsPageAddress(sendPageData?.id as number, patchData)
			.then((resp: any) => {
				sendsTemplates
					.mutateAsync({
						newData: { ...sendPageData, addresses: uiData },
					})
					.then(() => {
						UpdateAddress(nextValue, true);
					});
			})
			.catch((e: any) => console.error(e));
	};

	const HandleSend = (fileUuid: string) => {
		sendMail({
			fileUuid,
			notes: sendProcessData.form.notes,
			sendsPageId: sendProcessData.form.sendsPageId,
			addressId: sendProcessData.form.address,
			postageOptionId: sendProcessData.form.postage,
		})
			.then((s) => {
				setSendProcessData({
					...sendProcessData,
					sendDataActive: false,
					sendComplete: true,
					postingData: true,
					mailUuid: s.mailUuid,
				});
				ipmQueryClient.invalidateQueries({ queryKey: ['allSendsHistory'] }).then(() => null);
			})
			.catch((e) => {
				setSendError(e.toString());
				setSendProcessData({
					...sendProcessData,
					sendProcessOpen: false,
					uploadFileActive: false,
					sendDataActive: false,
					postingData: false,
				});
			});
	};

	const HandleUpload = () => {
		setSendProcessData({ ...sendProcessData, uploadFileActive: true, postingData: true });

		uploadFile({
			file: sendProcessData.form.file[0],
			fileCategory: UserFileCategory.MailSends,
		})
			.then((r) => {
				setSendProcessData({
					...sendProcessData,
					uploadFileActive: false,
					sendDataActive: true,
					postingData: true,
				});
				HandleSend(r.fileUuid);
			})
			.catch((e) => {
				setSendError(e.toString());
				setSendProcessData({
					...sendProcessData,
					sendProcessOpen: false,
					uploadFileActive: false,
					sendDataActive: false,
					postingData: false,
				});
			});
	};

	const HandleNewAddress = useCallback(
		async (singleAddress: AddressType) => {
			if (sendPageData) {
				const newPostageArray: Array<number> = [];
				sendPageData.addresses[0].postageOptions.forEach((thisPostage) => newPostageArray.push(thisPostage.id));

				const patchAddresses = [{ id: singleAddress.addressId, postageOptions: newPostageArray }];
				sendPageData.addresses.forEach((thisAddress) =>
					patchAddresses.push({
						id: thisAddress.id,
						postageOptions: thisAddress?.postageOptions.map((thisPostage) => thisPostage.id),
					}),
				);

				const newAddressUiData = [
					...sendPageData.addresses,
					{
						value: `${singleAddress.name}, ${singleAddress.addressLine1}, ${singleAddress.addressLine2}, ${singleAddress.postCode}, ${singleAddress.countryCode}`,
						id: singleAddress.addressId,
						postageOptions: [...sendPageData.addresses[0].postageOptions],
					},
				];

				await ForwardAddressChangeData(patchAddresses, newAddressUiData, singleAddress.addressId, 1);
			}
		},
		[sendPageData],
	);

	const HandleNewBulkAddresses = useCallback(
		async (bulkAddresses: Array<AddressType>) => {
			if (sendPageData) {
				const newPostageArray: Array<number> = [];
				sendPageData.addresses[0].postageOptions.forEach((thisPostage) => newPostageArray.push(thisPostage.id));

				const patchAddresses: Array<any> = [];
				sendPageData.addresses.forEach((thisAddress) =>
					patchAddresses.push({
						id: thisAddress.id,
						postageOptions: thisAddress?.postageOptions.map((thisPostage) => thisPostage.id),
					}),
				);

				const newBulkAddressesUiData: Array<SendsAddressDetailType> = [];

				bulkAddresses.forEach((item: AddressType) => {
					patchAddresses.push({
						id: item.addressId,
						postageOptions: newPostageArray,
					});

					newBulkAddressesUiData.push({
						id: item.addressId,
						value: item.addressString,
						postageOptions: [...sendPageData.addresses[0].postageOptions],
					});
				});

				const newAddressUiData = [...sendPageData.addresses, ...newBulkAddressesUiData];

				if (bulkAddresses.length !== 0) {
					await ForwardAddressChangeData(
						patchAddresses,
						newAddressUiData,
						bulkAddresses[0].addressId,
						bulkAddresses.length,
					);
				}
			}
		},
		[sendPageData],
	);

	const ResetSend = () => {
		formReset();
		ResetSendMail();
		setSendProcessData({
			address: address?.value,
			postage: postage?.value,
			form: {
				address: address?.id || -1,
				postage: postage?.id || -1,
				file: null,
			},

			sendProcessOpen: false,

			uploadFileActive: false,
			sendDataActive: false,

			postingData: false,
			sendComplete: false,
		});

		if (sendPageData && sendPageData.addresses.length === 1 && sendPageData.addresses[0].id === 100) {
			formMethods.setValue('address', sendPageData.addresses[0].id);
		}
	};

	useEffect(() => {
		if (pageId) {
			UpdatePageId(parseInt(pageId as string, 10));
			ResetSend();
		}
	}, [pageId]);

	useEffect(() => {
		document.title = sendPageData?.title
			? `${sendPageData.title} | UK Postbox`
			: 'Loading Sends Profile | UK Postbox';
		if (sendPageData && sendPageData.addresses.length === 1 && sendPageData.addresses[0].id === 100) {
			formMethods.setValue('address', sendPageData.addresses[0].id);
		}
	}, [sendPageData]);

	return (
		<FormProvider {...formMethods}>
			<div className='sticky'>
				{sendPageLoading ? (
					<div className='content-loader content-fader'>
						<div style={{ height: '53.5em' }} />
					</div>
				) : sendPageError ? (
					<StackVertical
						gap={FlexGap.ExtraSmall}
						align={FlexAlign.Center}
						justify={FlexJustify.Center}
						extraClass='p-xxl text-center'
					>
						<FontAwesomeIcon icon={faTriangleExclamation} size='3x' />
						<h6 className='sub-heading mt-sm mb-xs'>Error Loading Page: {pageId}</h6>
						<p>
							The requested page could not be found, or is no longer available. If you believe this to be
							incorrect, please contact the account owner.
						</p>
					</StackVertical>
				) : (
					<>
						{sendPageData?.isActive && (
							<div className='card'>
								<div className='card-body'>
									<form onSubmit={handleSubmit((data, e) => HandleProcessStart(data))}>
										<fieldset>
											<h4 className='mb-0'>
												{sendPageData.title ? sendPageData.title : 'Title'}
											</h4>
											<p>{sendPageData.description ? sendPageData.description : ''}</p>

											<hr />

											<div className='flex-column flex-gap-md'>
												{sendPageData.addresses[0]?.id === 100 ? (
													<AlertBanner
														title='Using Envelope Window'
														content='This form does not prevent duplicate file uploads. Double-check before clicking Send.'
													/>
												) : (
													<StackVertical gap={FlexGap.ExtraSmall}>
														<RestrictedAddressControl
															allowCreateAddress={sendPageData.allowCreateAddress}
															addAddressCallback={(thisAddress) =>
																HandleNewAddress(thisAddress)
															}
															addBulkAddressCallback={(addresses) =>
																HandleNewBulkAddresses(addresses)
															}
														/>
													</StackVertical>
												)}

												<RestrictedPostageControl />

												<SendsFileControl
													name='file'
													multiFile={sendPageData.addresses[0]?.id === 100}
												/>

												{sendError && <AlertBanner title={sendError} colour={Colour.Alert} />}

												<div className='sticky sticky-bottom pt-sm pb-sm'>
													<ButtonSubmit
														loading={sendProcessData.sendProcessOpen}
														title='Send'
														colour={ColourStateSolid.Primary}
														fullWidth
														disabled={!isValid}
													/>
												</div>
											</div>
										</fieldset>
									</form>
								</div>
							</div>
						)}

						{!sendPageData?.isActive && (
							<StackVertical
								gap={FlexGap.ExtraSmall}
								align={FlexAlign.Center}
								justify={FlexJustify.Center}
								extraClass='m-xxl p-xxl text-center'
							>
								<FontAwesomeIcon icon={faPaperPlane} size='3x' />
								<h6 className='sub-heading mt-sm mb-xs'>Page No Longer Active</h6>
								<p>If you believe this is an error, please contact your sends administrator?</p>
							</StackVertical>
						)}
					</>
				)}

				{sendProcessData.sendProcessOpen &&
					sendProcessData.address !== 'Window Envelope - Address as per letter' && (
						<SendProcessingWizardModal
							isOpen={sendProcessData.sendProcessOpen}
							sendData={sendProcessData}
							actionedCallback={() => HandleUpload()}
							closeCallback={() =>
								sendProcessData.postingData
									? ResetSend()
									: setSendProcessData({ ...sendProcessData, sendProcessOpen: false })
							}
							lockOpen={sendProcessData.postingData}
						/>
					)}

				{sendProcessData.sendProcessOpen &&
					sendProcessData.address === 'Window Envelope - Address as per letter' && (
						<SendMultipleProcessingWizardModal
							isOpen={sendProcessData.sendProcessOpen}
							sendData={sendProcessData}
							closeCallback={() => ResetSend()}
							lockOpen={sendProcessData.postingData}
						/>
					)}
			</div>
		</FormProvider>
	);
}

export default StandaloneSendForm;
